import { useEffect, useRef } from 'react'
import Icon from './Icon'

export interface DayEntry {
  /** "2026-08-01" in the day's own zone — the identity of the column. */
  key: string
  date: Date
  /** Show city for that day, when there is one. */
  city?: string | null
  count: number
}

/**
 * The horizontal day strip above the itinerary.
 *
 * One day at a time is how a day sheet is actually read on the road — the
 * whole-tour scroll is for planning, not for the morning of. The selected day
 * scrolls itself into view so switching by keyboard or from elsewhere in the
 * app doesn't leave the strip parked somewhere else.
 */
export default function DateScroller({
  days,
  selectedKey,
  onSelect,
}: {
  days: DayEntry[]
  selectedKey: string | null
  onSelect: (key: string) => void
}) {
  const trackRef = useRef<HTMLDivElement>(null)
  const selectedRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    selectedRef.current?.scrollIntoView({
      behavior: 'smooth',
      inline: 'center',
      block: 'nearest',
    })
  }, [selectedKey])

  function nudge(direction: -1 | 1) {
    trackRef.current?.scrollBy({ left: direction * 280, behavior: 'smooth' })
  }

  if (days.length === 0) return null

  return (
    <div className="row" style={{ gap: 6, alignItems: 'stretch' }}>
      <ScrollButton direction="left" onClick={() => nudge(-1)} />

      <div
        ref={trackRef}
        role="tablist"
        aria-label="Tour days"
        style={{
          display: 'flex',
          gap: 8,
          overflowX: 'auto',
          scrollbarWidth: 'none',
          padding: '2px 0',
          flex: 1,
          minWidth: 0,
        }}
      >
        {days.map((day) => {
          const selected = day.key === selectedKey
          return (
            <button
              key={day.key}
              ref={selected ? selectedRef : undefined}
              role="tab"
              aria-selected={selected}
              onClick={() => onSelect(day.key)}
              style={{
                flex: 'none',
                minWidth: 104,
                padding: '9px 14px',
                borderRadius: 'var(--radius-control)',
                textAlign: 'center',
                background: 'var(--panel)',
                // Outlined rather than filled. A solid accent pill is the
                // loudest thing on the screen and the selected day is not the
                // most important thing on it — the schedule underneath is.
                border: `${selected ? 'var(--signal-rule)' : 'var(--hairline-width)'} solid ${
                  selected ? 'var(--accent)' : 'var(--hairline)'
                }`,
                color: 'var(--text-primary)',
                fontWeight: selected ? 600 : 400,
              }}
            >
              <span
                style={{ display: 'block', fontSize: '0.86rem', fontWeight: 600 }}
              >
                {formatPill(day.date)}
              </span>
              <span
                className="t-spec-caption"
                style={{
                  display: 'block',
                  marginTop: 2,
                  color: selected ? 'var(--accent-text)' : 'var(--text-tertiary)',
                }}
              >
                {day.city || (day.count > 0 ? `${day.count} item${day.count === 1 ? '' : 's'}` : '—')}
              </span>
            </button>
          )
        })}
      </div>

      <ScrollButton direction="right" onClick={() => nudge(1)} />
    </div>
  )
}

function ScrollButton({
  direction,
  onClick,
}: {
  direction: 'left' | 'right'
  onClick: () => void
}) {
  return (
    <button
      onClick={onClick}
      aria-label={direction === 'left' ? 'Earlier days' : 'Later days'}
      className="panel"
      style={{
        display: 'grid',
        placeItems: 'center',
        width: 30,
        flex: 'none',
        color: 'var(--text-secondary)',
      }}
    >
      <Icon name={direction === 'left' ? 'chevronLeft' : 'chevronRight'} size={15} />
    </button>
  )
}

/** "Sat, Aug 1" */
function formatPill(date: Date): string {
  return new Intl.DateTimeFormat('en-GB', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  }).format(date)
}
